import {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import AdmicityTableV2 from '../shared-components/Table/V2/AdmicityTableV2';
import {formatDate} from '../utility/dateUtil';
import FILTER_OPERATIONS from '../constants/filterOperations';
import useNotification from '../utility/hooks/useNotification';
import {useLocation, useNavigate} from 'react-router-dom';
import {useGetApprovalsByApprovalPersonIdQuery, useSubmitApprovalMutation, useLazyGetApprovalHistoryByRequestIdQuery}
    from '../api/services/formApprovalsService';
import {Stack, styled, TextField} from '@mui/material';
import AdmicityStatus from '../shared-components/AdmicityStatus';
import AdmicityDialog from './AdmicityDialog';
import {APPROVAL_STATUSES, getApprovalStatusInfo} from '../constants/FormApprovalStatuses';
import {FORM_STATUSES} from '../constants/formStatuses';
import {FORM_REQUEST_STATUSES} from '../constants/formRequestStatuses';
import DateRangePicker from '../shared-components/Table/V2/components/columnFilters/DateRangePicker';
import Tooltip from '@mui/material/Tooltip';
import withPaginationV2 from '../hoc/pagination/withPaginationV2';

const StyledCell = styled('div')({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: '24px',
});

const CustomCell = ({cell}) => {
    const value = cell.getValue();
    return (
        <Tooltip title={value} arrow>
            <StyledCell>{value}</StyledCell>
        </Tooltip>
    );
};
CustomCell.propTypes = {cell: PropTypes.object.isRequired};

const ApprovalFormsTable = ({employeeId}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const {showInfoNotification, showErrorNotification} = useNotification();

    const Table = useMemo(
        () =>
            withPaginationV2(AdmicityTableV2, {
                func: useGetApprovalsByApprovalPersonIdQuery,
                props: {employeeId},
            }),
        [employeeId]
    );

    const [fetchHistory, {
        data: historyData,
        isFetching: isHistoryFetching,
        isError: isHistoryError,
        error: historyError
    }] =
        useLazyGetApprovalHistoryByRequestIdQuery();
    const [submitApproval] = useSubmitApprovalMutation();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [note, setNote] = useState('');
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [selectedAction, setSelectedAction] = useState(null);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [historyRows, setHistoryRows] = useState([]);
    useMemo(() => {
        if (historyData) {
            setHistoryRows(Array.isArray(historyData) ? historyData : (historyData.items ?? []));
        }
    }, [historyData]);
    const handleAction = async (status) => {
        if (!selectedAssignment?.request) return;
        const result = await submitApproval({
            formId: selectedAssignment.stage.formId,
            assignmentId: selectedAssignment.id,
            status,
            note,
            employeeId,
        });

        if (result.error) {
            showErrorNotification(`Failed to ${status.toLowerCase()} form.`);
        } else {
            showInfoNotification(`Form ${status.toLowerCase()} successfully.`);
            setDialogOpen(false);
            setNote('');
        }
    };

    const columns = useMemo(
        () => [
            {accessorKey: 'stage.name', header: 'Approval Stage', filterFn: FILTER_OPERATIONS.CONTAINS},
            {
                id: 'stageProgress',
                header: 'Approval Progress',
                accessorFn: (row) => `Stage ${row.stageNumber} / ${row.stageCount}`,
                enableColumnFilter: false,
            },
            {
                accessorKey: 'status',
                header: 'Approval Status',
                // eslint-disable-next-line react/prop-types
                Cell: ({cell: {getValue}}) => <AdmicityStatus {...getApprovalStatusInfo(getValue())} />,
                filterVariant: 'select',
                filterFn: FILTER_OPERATIONS.EQUALS,
                filterSelectOptions: Object.values(APPROVAL_STATUSES).map((s) => ({value: s, label: s})),
            },
            {accessorKey: 'request.formName', header: 'Form Name', filterFn: FILTER_OPERATIONS.CONTAINS},
            {accessorKey: 'request.editedBy.fullname', header: 'Submitted By', filterFn: FILTER_OPERATIONS.CONTAINS},
            {
                accessorKey: 'request.editedAt',
                header: 'Submitted At',
                Cell: ({cell}) => (cell.getValue() ? formatDate(cell.getValue()) : ''),
                Filter: DateRangePicker,
                filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE,
            },
            {accessorKey: 'approvedBy', header: 'Approved By', filterFn: FILTER_OPERATIONS.CONTAINS},
            {
                accessorKey: 'approvedAt',
                header: 'Approved At',
                Cell: ({cell}) => (cell.getValue() ? formatDate(cell.getValue()) : ''),
                Filter: DateRangePicker,
                filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE,
            },
            {
                accessorKey: 'approvalNote',
                header: 'Approval Note',
                filterFn: FILTER_OPERATIONS.CONTAINS,
                Cell: CustomCell
            },
        ],
        []
    );

    const historyColumns = useMemo(
        () => [
            {accessorKey: 'stage.name', header: 'Stage Name'},
            {
                id: 'stageProgress',
                header: 'Progress',
                accessorFn: (row) => `Stage ${row.stageNumber} / ${row.stageCount}`
            },
            {
                accessorKey: 'status',
                header: 'Status',
                // eslint-disable-next-line react/prop-types
                Cell: ({cell: {getValue}}) => <AdmicityStatus {...getApprovalStatusInfo(getValue())} />
            },
            {accessorKey: 'approvedBy', header: 'Approved By'},
            {
                accessorKey: 'approvedAt',
                header: 'Approved At',
                Cell: ({cell}) => (cell.getValue() ? formatDate(cell.getValue()) : '')
            },
            {accessorKey: 'approvalNote', header: 'Note', Cell: CustomCell},
        ],
        []
    );

    const rowActions = (row) => {
        const isFormActive = row.request?.form?.status === FORM_STATUSES.active;
        const isSubmitted = row.request?.status === FORM_REQUEST_STATUSES.submitted;
        const canShowHistory = (row.stageCount ?? 1) > 1;

        return [
            {
                label: 'Approve',
                disabled: () => !isFormActive || !isSubmitted || row.status === 'Approved',
                action: () => {
                    setSelectedAssignment(row);
                    setSelectedAction('Approved');
                    setDialogOpen(true);
                },
            },
            {
                label: 'Reject',
                disabled: () => !isFormActive || !isSubmitted || row.status === 'Approved' || row.status === 'Rejected',
                action: () => {
                    setSelectedAssignment(row);
                    setSelectedAction('Rejected');
                    setDialogOpen(true);
                },
            },
            {
                label: 'History',
                disabled: () => !canShowHistory,
                action: async () => {
                    setHistoryDialogOpen(true);
                    try {
                        await fetchHistory({requestId: row.request.id, currentStageNumber: row.stageNumber}, true);
                    } catch (e) {
                    }
                }
            }
        ];
    };

    const handleRowClick = ({stage, request}) => {
        if (location.pathname.includes('/profiles/employees/')) {
            navigate(`/profiles/employees/${employeeId}/forms/${request.id}/approval`);
        } else {
            navigate(`/request-and-responses/approval/${stage.formId}/${request.id}`);
        }
    };

    return (
        <>
            <Table
                id={`approvalForms-${employeeId}`}
                columns={columns}
                onRowClick={handleRowClick}
                rowActions={rowActions}
                noRowsOverlay={{icon: null, text: 'No approvals pending.'}}
                tableProps={{
                    enableSorting: false,
                    enableFilters: true,
                    manualFiltering: true,
                    manualPagination: true,
                }}
            />

            <AdmicityDialog
                open={dialogOpen}
                handleClose={() => setDialogOpen(false)}
                title={selectedAction === 'Approved' ? 'Approval Note' : 'Rejection Note'}
                maxWidth="sm"
                actions={[
                    {label: 'Cancel', onClick: () => setDialogOpen(false)},
                    {label: selectedAction, onClick: () => handleAction(selectedAction), disabled: false},
                ]}
            >
                <Stack spacing={2} mt={1}>
                    <TextField
                        multiline
                        fullWidth
                        minRows={3}
                        label="Optional Note"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                    />
                </Stack>
            </AdmicityDialog>

            <AdmicityDialog
                open={historyDialogOpen}
                handleClose={() => setHistoryDialogOpen(false)}
                title="Approval History"
                maxWidth="md"
                actions={[
                    {label: 'Close', onClick: () => setHistoryDialogOpen(false)}
                ]}
            >
                <AdmicityTableV2
                    id={`approval-history-table-${employeeId}`}
                    columns={historyColumns}
                    data={historyRows}
                    totalItemCount={historyRows.length}
                    pagination={{pageIndex: 0, pageSize: historyRows.length}}
                    isLoading={isHistoryFetching}
                    isRefetching={false}
                    onPaginationModelChange={() => {
                    }}
                    tableProps={{
                        enableSorting: false,
                        enableFilters: false,
                        enablePagination: false,
                        enableBottomToolbar: false,
                        enableTopToolbar: false,
                        enableStickyHeader: true,
                        enableStickyFooter: false,
                        muiTablePaperProps: {
                            sx: {
                                display: 'block',
                            }
                        },
                        muiTableContainerProps: {
                            sx: {
                                maxHeight: 400,
                                overflow: 'auto',
                            }
                        }
                    }}
                    noRowsOverlay={{icon: null, text: isHistoryFetching ? 'Loading…' : 'No history yet.'}}
                />
                {isHistoryError && (
                    <div style={{paddingTop: 8, color: '#b00020'}}>
                        Failed to load history{historyError?.data?.title ? `: ${historyError.data.title}` : ''}.
                    </div>
                )}
            </AdmicityDialog>

        </>
    );
};

ApprovalFormsTable.propTypes = {
    employeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ApprovalFormsTable;
